import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import {
  computeAnchoredPanelStyle,
  type AnchoredAlign,
  type AnchoredPlacement,
} from "./anchoredPanelPosition";

/** Focusable panel items; disabled ones are skipped by keyboard navigation. */
const ENABLED = ':not(:disabled):not([aria-disabled="true"])';
const MENU_ITEM_SELECTOR = [
  `[role="menuitem"]${ENABLED}`,
  `[role="option"]${ENABLED}`,
  `button${ENABLED}`,
].join(", ");

export type AnchoredPanelFocusMode = "menu" | "listbox" | false;

export interface UseAnchoredPanelOptions {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  placement?: AnchoredPlacement;
  align?: AnchoredAlign;
  gap?: number;
  /** Menu/listbox keyboard navigation and focus restore. */
  focusMode?: AnchoredPanelFocusMode;
  /** Close on outside pointer down. Default true when focusMode is set. */
  closeOnOutsideClick?: boolean;
  /** Non-interactive panel (tooltips, copy badge): no outside-click close, Escape is not consumed. */
  passive?: boolean;
}

function getPanelItems(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR));
}

export function useAnchoredPanel({
  isOpen,
  onClose,
  triggerRef,
  panelRef,
  placement = "below",
  align = "start",
  gap = 8,
  focusMode = false,
  closeOnOutsideClick,
  passive = false,
}: UseAnchoredPanelOptions) {
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({
    position: "fixed",
  });
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const shouldCloseOnOutside =
    closeOnOutsideClick ?? (focusMode !== false && !passive);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger) {
      return;
    }

    const { style } = computeAnchoredPanelStyle({
      triggerRect: trigger.getBoundingClientRect(),
      panelWidth: panel?.offsetWidth ?? 0,
      panelHeight: panel?.offsetHeight ?? 0,
      placement,
      align,
      gap,
    });
    setPanelStyle(style);
  }, [align, gap, panelRef, placement, triggerRef]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleReposition = () => updatePosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen || !shouldCloseOnOutside) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      onCloseRef.current();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, panelRef, shouldCloseOnOutside, triggerRef]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      // Passive panels (tooltips) must be dismissible too (WCAG 1.4.13), but
      // they leave the key unconsumed for the surrounding layer.
      if (!passive) {
        event.preventDefault();
      }
      onCloseRef.current();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, passive]);

  useEffect(() => {
    if (!isOpen || !focusMode) {
      return;
    }

    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) {
      return;
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const focusFrame = requestAnimationFrame(() => {
      const items = getPanelItems(panel);
      items[0]?.focus();
    });

    const handlePanelKeyDown = (event: KeyboardEvent) => {
      const items = getPanelItems(panel);
      if (items.length === 0) {
        return;
      }

      const active = document.activeElement as HTMLElement | null;
      const currentIndex = active ? items.indexOf(active) : -1;

      if (event.key === "Tab") {
        event.preventDefault();
        onCloseRef.current();
        trigger.focus();
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const next =
          currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
        items[next]?.focus();
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        const next =
          currentIndex <= 0
            ? items.length - 1
            : currentIndex - 1;
        items[next]?.focus();
        return;
      }

      if (event.key === "Home") {
        event.preventDefault();
        items[0]?.focus();
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        items[items.length - 1]?.focus();
      }
    };

    panel.addEventListener("keydown", handlePanelKeyDown);

    return () => {
      cancelAnimationFrame(focusFrame);
      panel.removeEventListener("keydown", handlePanelKeyDown);

      if (trigger.isConnected) {
        trigger.focus();
        return;
      }
      const previous = previouslyFocusedRef.current;
      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [focusMode, isOpen, panelRef, triggerRef]);

  return { panelStyle, updatePosition };
}
